import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Building2, Mail, Lock, User, Phone, Loader2, ArrowRight, Shield, Zap, Clock, CalendarDays } from "lucide-react";
import usePageTitle from "@/hooks/usePageTitle";

const API = (process.env.REACT_APP_BACKEND_URL || window.location.origin);

export default function RegisterPage() {
  usePageTitle("Creer une agence GLOTECH OS");
  const navigate = useNavigate();
  const { login } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) {
      setError("Veuillez remplir tous les champs obligatoires.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/saas/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || "Erreur lors de l'inscription.");
        return;
      }
      // Auto-login
      await login(form.email, form.password);
      navigate("/app/onboarding");
    } catch (e) {
      setError("Erreur serveur. Veuillez reessayer.");
    } finally {
      setLoading(false);
    }
  };

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  return (
    <div className="min-h-screen bg-[#080C18] flex" data-testid="register-page">
      {/* Left: Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-[#0B0F19] to-[#0F1629] flex-col justify-center px-16">
        <div className="max-w-lg">
          <h1 className="text-4xl font-bold text-white mb-4">
            Lancez votre agence sur <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">GLOTECH OS</span>
          </h1>
          <p className="text-slate-400 text-lg mb-10">
            La plateforme tout-en-un pour gerer votre agence digitale. CRM, projets, facturation, SEO - tout est inclus.
          </p>
          <div className="space-y-4">
            {[
              { icon: Clock, text: "7 jours d'essai gratuit, sans engagement" },
              { icon: Zap, text: "Pret en 2 minutes, aucune carte requise" },
              { icon: Shield, text: "Vos donnees sont 100% isolees et securisees" },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-cyan-500/10 flex items-center justify-center">
                  <item.icon className="w-4 h-4 text-cyan-400" />
                </div>
                <span className="text-slate-300 text-sm">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right: Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-8 lg:hidden">
            <h1 className="text-2xl font-bold text-white">GLOTECH <span className="text-cyan-400">OS</span></h1>
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Creer votre compte</h2>
          <p className="text-slate-400 mb-8">Essai gratuit de 7 jours, aucune carte requise.</p>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm rounded-lg p-3 mb-6" data-testid="register-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm text-slate-300 block mb-1.5">Nom de l'agence *</label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input data-testid="register-name" value={form.name} onChange={set("name")} className="w-full bg-[#0B0F19] border border-slate-700 rounded-lg pl-10 pr-4 py-3 text-white focus:border-cyan-500 outline-none" placeholder="Mon Agence Digitale" />
              </div>
            </div>
            <div>
              <label className="text-sm text-slate-300 block mb-1.5">Email *</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input data-testid="register-email" value={form.email} onChange={set("email")} type="email" className="w-full bg-[#0B0F19] border border-slate-700 rounded-lg pl-10 pr-4 py-3 text-white focus:border-cyan-500 outline-none" placeholder="admin@monagence.fr" />
              </div>
            </div>
            <div>
              <label className="text-sm text-slate-300 block mb-1.5">Mot de passe *</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input data-testid="register-password" value={form.password} onChange={set("password")} type="password" className="w-full bg-[#0B0F19] border border-slate-700 rounded-lg pl-10 pr-4 py-3 text-white focus:border-cyan-500 outline-none" placeholder="Minimum 6 caracteres" />
              </div>
            </div>
            <div>
              <label className="text-sm text-slate-300 block mb-1.5">Telephone</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input data-testid="register-phone" value={form.phone} onChange={set("phone")} className="w-full bg-[#0B0F19] border border-slate-700 rounded-lg pl-10 pr-4 py-3 text-white focus:border-cyan-500 outline-none" placeholder="06 XX XX XX XX" />
              </div>
            </div>
            <button
              data-testid="register-submit"
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 text-white py-3 rounded-lg font-bold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50 mt-6"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Commencer l'essai gratuit <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>

          <p className="text-center text-slate-500 text-sm mt-6">
            Deja un compte ?{" "}
            <Link to="/login" className="text-cyan-400 hover:text-cyan-300 transition-colors">
              Se connecter
            </Link>
          </p>

          {/* Event Center redirect banner */}
          <Link to="/event" className="mt-4 flex items-center gap-3 p-3 rounded-xl border border-amber-500/20 bg-amber-500/5 hover:bg-amber-500/10 transition-colors" data-testid="event-center-redirect-banner">
            <CalendarDays className="h-5 w-5 text-amber-400 shrink-0" />
            <div>
              <p className="text-amber-400 text-sm font-medium">Organisateur d'evenements ?</p>
              <p className="text-slate-500 text-xs">Demandez plutot un acces GLOTECH Event Center</p>
            </div>
            <ArrowRight className="h-4 w-4 text-amber-400 ml-auto shrink-0" />
          </Link>
        </div>
      </div>
    </div>
  );
}
